import React, { useState, useRef } from 'react';
import clientCandidateDataService from '../../../services/clientCandidateDataService';

/**
 * ClientDocumentsWidget
 * Upload, categorize, and manage candidate documents
 * Supports drag-and-drop and click-to-upload
 */

const DOCUMENT_TYPES = [
  { value: 'resume', label: 'Resume', icon: '📄' },
  { value: 'cover_letter', label: 'Cover Letter', icon: '✉️' },
  { value: 'portfolio', label: 'Portfolio', icon: '🎨' },
  { value: 'certificate', label: 'Certificate', icon: '🏆' },
  { value: 'reference', label: 'Reference Letter', icon: '📝' },
  { value: 'other', label: 'Other', icon: '📎' }
];

const ClientDocumentsWidget = ({ profile, candidateId, onRefresh }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('resume');
  const [documentNotes, setDocumentNotes] = useState('');
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFile(files[0]);
      setShowUploadForm(true);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setShowUploadForm(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('document_type', documentType);
      formData.append('notes', documentNotes);

      await clientCandidateDataService.createDocument(candidateId, formData);
      
      // Reset form
      setSelectedFile(null);
      setDocumentType('resume');
      setDocumentNotes('');
      setShowUploadForm(false);
      
      if (onRefresh) {
        onRefresh();
      }
      
      alert('Document uploaded successfully!');
    } catch (error) {
      console.error('Error uploading document:', error);
      alert('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Delete this document?')) return;
    
    try {
      await clientCandidateDataService.deleteDocument(candidateId, documentId);
      if (onRefresh) {
        onRefresh();
      }
      alert('Document deleted successfully');
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getDocumentIcon = (type) => {
    const docType = DOCUMENT_TYPES.find(dt => dt.value === type);
    return docType ? docType.icon : '📎';
  };

  const getDocumentLabel = (type) => {
    const docType = DOCUMENT_TYPES.find(dt => dt.value === type);
    return docType ? docType.label : 'Document';
  };

  return (
    <div className="space-y-4">
      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          isDragging 
            ? 'border-emerald-500 bg-emerald-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg"
        />
        
        <div className="space-y-2">
          <div className="text-4xl">📁</div>
          <div className="text-sm text-gray-600">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-emerald-700 hover:text-emerald-700 font-medium"
            >
              Click to upload
            </button>
            {' '}or drag and drop
          </div>
          <div className="text-xs text-gray-500">
            PDF, DOC, DOCX, TXT, PNG, JPG (max 10MB)
          </div>
        </div>
      </div>

      {/* Upload Form */}
      {showUploadForm && selectedFile && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-3">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="font-medium text-sm text-gray-900">{selectedFile.name}</div>
              <div className="text-xs text-gray-600">{formatFileSize(selectedFile.size)}</div>
            </div>
            <button
              onClick={() => {
                setSelectedFile(null);
                setShowUploadForm(false);
              }}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Document Type
            </label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {DOCUMENT_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={documentNotes}
              onChange={(e) => setDocumentNotes(e.target.value)}
              placeholder="Add any notes about this document..."
              rows={2}
              className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>

          <button
            onClick={handleUpload}
            disabled={uploading}
            className="w-full px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {uploading ? 'Uploading...' : 'Upload Document'}
          </button>
        </div>
      )}

      {/* Documents List */}
      <div>
        <h4 className="text-xs font-semibold text-gray-700 mb-2">
          Uploaded Documents ({profile.client_documents?.length || 0})
        </h4>
        
        {profile.client_documents && profile.client_documents.length > 0 ? (
          <div className="space-y-2">
            {profile.client_documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors group"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="text-2xl flex-shrink-0">
                    {getDocumentIcon(doc.document_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {doc.filename}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <span className="px-2 py-0.5 bg-white rounded-full border">
                        {getDocumentLabel(doc.document_type)}
                      </span>
                      <span>•</span>
                      <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                      {doc.file_size && (
                        <>
                          <span>•</span>
                          <span>{formatFileSize(doc.file_size)}</span>
                        </>
                      )}
                    </div>
                    {doc.notes && (
                      <div className="text-xs text-gray-600 mt-1 italic">
                        {doc.notes}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-2 ml-3">
                  {doc.file_path && (
                    <a
                      href={doc.file_path}
                      download
                      className="p-1 text-emerald-700 hover:text-emerald-700 hover:bg-emerald-50 rounded"
                      title="Download"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                    </a>
                  )}
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-opacity"
                    title="Delete"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 text-sm">
            <div className="text-3xl mb-2">📭</div>
            <div>No documents uploaded yet</div>
            <div className="text-xs">Upload a document to get started</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDocumentsWidget;
